import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { AppConfig } from "../../app-config";
import { tap } from "rxjs/operators";
import { TokenStorageService } from "../auth/token-storage.service";

const httpOptions = {
    headers: new HttpHeaders({ "Content-Type": "application/json" }),
    observe: "response" as "body",
};

@Injectable({
    providedIn: "root",
})
export class DebidaDiligenciaService {
    AUTH_API = "";

    constructor(
        private http: HttpClient,
        private tokenStorage: TokenStorageService,
        private appConfig: AppConfig
    ) {
        this.AUTH_API = appConfig.api_url;
    }

    count(filtros): Observable<any> {
        return this.http
            .post(
                this.AUTH_API + "debida-diligencia/count",
                filtros.value,
                httpOptions
            )
            .pipe(
                tap((evaluacion) => {
                    return evaluacion;
                })
            );
    }

    getAll(filtros): Observable<any> {
        return this.http
            .post(
                this.AUTH_API + "debida-diligencia/all",
                filtros.value,
                httpOptions
            )
            .pipe(
                tap((evaluacion) => {
                    return evaluacion;
                })
            );
    }

    getAllByCliente(clienteId, pagina, cantidad): Observable<any> {
        return this.http
            .post(
                this.AUTH_API +
                    `debida-diligencia/cliente/${clienteId}/all?pagina=${pagina}&cantidad=${cantidad}`,
                {},
                httpOptions
            )
            .pipe(
                tap((evaluacion) => {
                    return evaluacion;
                })
            );
    }

    countByCliente(clienteId): Observable<any> {
        return this.http
            .get(
                this.AUTH_API + `debida-diligencia/cliente/${clienteId}/count`,
                httpOptions
            )
            .pipe(
                tap((cantidad) => {
                    return cantidad;
                })
            );
    }

    downloadPDF(ddId): Observable<any> {
        return this.http
            .get(this.AUTH_API + `debida-diligencia/${ddId}/pdf`, httpOptions)
            .pipe(
                tap((cantidad) => {
                    return cantidad;
                })
            );
    }

    downloadPDF2(id: number): Observable<Blob> {
        return this.http.get(this.AUTH_API + `debida-diligencia/${id}/pdf`, {
            responseType: "blob",
        });
    }

    getBySujetoObligado(sujetoObligadoId, filtros, pagina, cantidad): Observable<any> {
        return this.http
            .post(
                this.AUTH_API +
                    `debida-diligencia/sujeto-obligado/${sujetoObligadoId}/all?pagina=${pagina}&cantidad=${cantidad}`,
                filtros.value,
                httpOptions
            )
            .pipe(
                tap((evaluacion) => {
                    return evaluacion;
                })
            );
    }

    getAllByUser(filtros): Observable<any> {
        return this.http
            .post(
                this.AUTH_API + "debida-diligencia/by-user",
                filtros.value,
                httpOptions
            )
            .pipe(
                tap((analisis) => {
                    return analisis;
                })
            );
    }

    get(id): Observable<any> {
        return this.http.get(
            this.AUTH_API + "debida-diligencia/" + id,
            httpOptions
        );
    }

    getByUUID(uuid): Observable<any> {
        return this.http.get(
            this.AUTH_API + "debida-diligencia/uuid/" + uuid,
            httpOptions
        );
    }

    getFormByUUIDAndId(uuid, id): Observable<any> {
        return this.http.get(
            this.AUTH_API + `debida-diligencia/uuid/${uuid}/form/${id}`,
            httpOptions
        );
    }

    register(analisis): Observable<any> {
        return this.http.post(
            this.AUTH_API + "debida-diligencia/add",
            analisis,
            httpOptions
        );
    }

    delete(id): Observable<any> {
        return this.http.delete(
            this.AUTH_API + "debida-diligencia/" + id,
            httpOptions
        );
    }

    edit(id, analisis): Observable<any> {
        return this.http.put(
            this.AUTH_API + "debida-diligencia/" + id,
            analisis,
            httpOptions
        );
    }

    addRespuesta(ddHash, opcionId, respuesta): Observable<any> {
        return this.http.put(
            this.AUTH_API +
                `debida-diligencia/respuesta/${ddHash}/opcion/${opcionId}`,
            respuesta,
            httpOptions
        );
    }

    addRespuestaValor(ddHash, opcionId, valor): Observable<any> {
        return this.http.put(
            this.AUTH_API +
                `debida-diligencia/respuesta/${ddHash}/valor/${opcionId}`,
            valor,
            httpOptions
        );
    }

    addConclusion(id: any, conclusion: string): Observable<any> {
        // Configura los parámetros de la solicitud
        const params = new HttpParams().set("conclusion", conclusion);

        return this.http.post(
            this.AUTH_API + `debida-diligencia/${id}/conclusion`,
            null,
            {
                params: params,
                responseType: "text",
            }
        );
    }

    addFormulario(uuid: string, formulario: any): Observable<any> {
        return this.http.post(
            this.AUTH_API + `debida-diligencia/uuid/${uuid}/formulario/add`,
            formulario,
            httpOptions
        );
    }

    addRespuestaCheckbox(ddHash, opcionId, valor): Observable<any> {
        return this.http.put(
            this.AUTH_API +
                `debida-diligencia/respuesta/${ddHash}/cumple-no-cumple/${opcionId}`,
            valor,
            httpOptions
        );
    }

    addRespuestaAdjuntos(ddHash, opcionId, adjuntos): Observable<any> {
        // Construir el cuerpo de la solicitud como FormData
        const formData = new FormData();
        // Adjuntar los archivos al FormData
        for (let i = 0; i < adjuntos.length; i++) {
            formData.append("files[]", adjuntos[i]);
        }

        // Establecer las cabeceras para multipart form data
        const httpOptions = {
            headers: new HttpHeaders({
                enctype: "multipart/form-data",
                Accept: "application/json",
            }),
        };

        // Realizar la solicitud utilizando FormData como cuerpo
        return this.http.put(
            `${this.AUTH_API}debida-diligencia/respuesta/${ddHash}/adjunto/${opcionId}`,
            formData,
            httpOptions
        );
    }

    addRespuestaTextoLibre(ddHash, opcionId, respuesta): Observable<any> {
        return this.http.put(
            this.AUTH_API +
                `debida-diligencia/respuesta/${ddHash}/objetivo/${opcionId}`,
            respuesta,
            httpOptions
        );
    }

    editStatus(id, status): Observable<any> {
        return this.http.put(
            this.AUTH_API + "debida-diligencia/" + id + "/estado",
            status,
            httpOptions
        );
    }

    editStatusRechazo(id, status, motivo): Observable<any> {
        return this.http.put(
            this.AUTH_API + "debida-diligencia/" + id + "/rechazo",
            motivo,
            httpOptions
        );
    }

    editTablaDeEvaluacion(id, tipo): Observable<any> {
        return this.http.put(
            this.AUTH_API + "debida-diligencia/" + id + "/tabla-de-evaluacion",
            tipo,
            httpOptions
        );
    }

    editCliente(id, estructura): Observable<any> {
        return this.http.put(
            this.AUTH_API + "debida-diligencia/" + id + "/cliente",
            estructura,
            httpOptions
        );
    }

    editResponsable(id, owner): Observable<any> {
        return this.http.put(
            this.AUTH_API + "debida-diligencia/" + id + "/responsable",
            owner,
            httpOptions
        );
    }

    editFormulario(id, escenario): Observable<any> {
        return this.http.put(
            this.AUTH_API + "debida-diligencia/" + id + "/formulario",
            escenario,
            httpOptions
        );
    }

    deleteFormulario(id): Observable<any> {
        return this.http.delete(
            this.AUTH_API + "formulario/persona-formulario/" + id,
            httpOptions
        );
    }

    editFechaFin(id, fechaF): Observable<any> {
        return this.http.put(
            this.AUTH_API + "debida-diligencia/" + id + "/fecha-fin",
            fechaF,
            httpOptions
        );
    }

    editNombre(id, nombre): Observable<any> {
        return this.http.put(
            this.AUTH_API + "debida-diligencia/" + id + "/nombre",
            nombre,
            httpOptions
        );
    }

    copiar(id, nombre): Observable<any> {
        var httpOptionsLocal = {
            headers: new HttpHeaders({
                "Content-Type": "application/x-www-form-urlencoded",
            }),
            //observe: 'response' as 'body'
        };

        return this.http.post(
            this.AUTH_API + "debida-diligencia/" + id + "/copiar",
            {},
            { ...httpOptionsLocal, params: { nombre: nombre } }
        );
    }
}
